import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import { logError } from "@/lib/logger";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileUpload } from "../FileUpload";
import { BlogSeoFields } from "./blog/BlogSeoFields";
import { RichTextEditor } from "@/components/RichTextEditor";
import {
  FileText,
  Image,
  Trash2,
  Plus,
  Edit,
  X,
  Upload,
  Eye,
} from "lucide-react";

const toLocalInput = (iso) => {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const BlogManager = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ 
    title: "", slug: "", excerpt: "", content: "", featured_image: "", published: false, seo_title: "", seo_description: "", seo_image: "", scheduled_at: "" 
  });

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/blog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleImageUpload = (url) => {
    const fullUrl = url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
    setFormData({ ...formData, featured_image: fullUrl });
  };

  const savePost = async (e, { preview = false } = {}) => {
    e.preventDefault();
    const data = { ...formData, slug: formData.slug || generateSlug(formData.title), scheduled_at: formData.scheduled_at && !formData.published ? new Date(formData.scheduled_at).toISOString() : null };
    if (!(data.title && data.excerpt && data.content)) {
      toast.error(preview ? "Add a title, excerpt and content before previewing" : "Please add some content to your post");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/blog/${editingId}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Post updated!");
      } else {
        await axios.post(`${API}/admin/blog`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Post created!");
      }
      if (preview) window.open(`/blog/${data.slug}?preview=1`, "_blank", "noopener");
      setShowForm(false);
      setEditingId(null);
      setFormData({ title: "", slug: "", excerpt: "", content: "", featured_image: "", published: false, seo_title: "", seo_description: "", seo_image: "", scheduled_at: "" });
      fetchPosts();
    } catch (error) {
      toast.error("Failed to save post");
    }
  };

  const editPost = (post) => {
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      featured_image: post.featured_image || "",
      published: post.published,
      seo_title: post.seo_title || "",
      seo_description: post.seo_description || "",
      seo_image: post.seo_image || "",
      scheduled_at: post.scheduled_at ? toLocalInput(post.scheduled_at) : ""
    });
    setEditingId(post.id);
    setShowForm(true);
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Post deleted");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-blog-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-charcoal">Blog Manager</h1>
          <p className="font-body text-stone-grey">Create and manage your blog posts</p>
        </div>
        <Button onClick={() => { setShowForm(!showForm); setEditingId(null); setFormData({ title: "", slug: "", excerpt: "", content: "", featured_image: "", published: false, seo_title: "", seo_description: "", seo_image: "", scheduled_at: "" }); }} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {showForm && (
        <div className="glass rounded-xl p-6 mb-8">
          <form onSubmit={savePost} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-body text-sm text-stone-grey mb-2 block">Title</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value, slug: generateSlug(e.target.value)})}
                  placeholder="My Amazing Post"
                  required
                  className="form-input"
                />
              </div>
              <div>
                <Label className="font-body text-sm text-stone-grey mb-2 block">URL Slug</Label>
                <Input 
                  value={formData.slug} 
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  placeholder="my-amazing-post"
                  className="form-input"
                />
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Featured Image</Label>
              <FileUpload onUpload={handleImageUpload} label="Upload featured image" />
              {formData.featured_image && (
                <div className="mt-2 border rounded-lg p-2 bg-sand/20 flex items-center gap-4">
                  <img src={formData.featured_image} alt="Preview" className="h-20 rounded" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({...formData, featured_image: ""})}>
                    <X className="w-4 h-4" /> Remove
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Excerpt (Short Description)</Label>
              <Textarea 
                value={formData.excerpt} 
                onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                placeholder="A brief summary of your post..."
                required
                rows={2}
                className="form-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Content</Label>
              <RichTextEditor value={formData.content} onChange={(html) => setFormData((prev) => ({ ...prev, content: html }))} />
              <p className="text-xs text-taupe mt-1">Use the toolbar for headings, bold, links and images — or switch to HTML source for full control.</p>
            </div>

            <BlogSeoFields formData={formData} setFormData={setFormData} />
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="published"
                checked={formData.published} 
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="published" className="font-body text-sm text-stone-grey">Publish immediately</Label>
            </div>
            {!formData.published && (
              <div className="rounded-lg border border-sand bg-sand/10 p-4" data-testid="blog-schedule-block">
                <Label htmlFor="scheduled_at" className="font-body text-sm text-stone-grey mb-2 block">Or schedule it to go live automatically</Label>
                <div className="flex flex-wrap items-center gap-3">
                  <Input id="scheduled_at" type="datetime-local" data-testid="blog-schedule-input" value={formData.scheduled_at} min={toLocalInput(new Date().toISOString())}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })} className="form-input w-auto" />
                  {formData.scheduled_at && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, scheduled_at: "" })} data-testid="blog-schedule-clear">Clear</Button>
                  )}
                </div>
                <p className="text-xs text-taupe mt-2">{formData.scheduled_at ? `Will publish ${new Date(formData.scheduled_at).toLocaleString()} (your local time).` : "Leave empty to keep it as a draft."}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button type="submit" className="btn-primary">{editingId ? "Update" : "Create"} Post</Button>
              <Button type="button" variant="outline" onClick={(e) => savePost(e, { preview: true })} data-testid="blog-save-preview-btn">
                <Eye className="w-4 h-4 mr-2" /> Save & Preview
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : posts.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-taupe mx-auto mb-4" />
          <p className="font-body text-stone-grey">No blog posts yet. Write your first one!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="glass rounded-xl p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span data-testid={`blog-status-${post.id}`} className={`text-xs px-2 py-1 rounded ${post.published ? 'bg-jade-soft/30 text-jade-text' : post.scheduled_at ? 'bg-champagne-gold/20 text-charcoal' : 'bg-sand text-taupe'}`}>
                      {post.published ? "Published" : post.scheduled_at ? `Scheduled · ${new Date(post.scheduled_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}` : "Draft"}
                    </span>
                    <span className="text-xs text-taupe">{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                  <h3 className="font-heading text-lg text-charcoal">{post.title}</h3>
                  <p className="font-body text-sm text-stone-grey mt-1">{post.excerpt}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" asChild data-testid={`blog-preview-btn-${post.id}`}>
                    <a href={`/blog/${post.slug}?preview=1`} target="_blank" rel="noopener noreferrer"><Eye className="w-4 h-4" /></a>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => editPost(post)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deletePost(post.id)}>
                    <Trash2 className="w-4 h-4 text-lipstick-red" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

